import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { DollarSign, Loader2, CheckCircle, XCircle, AlertCircle, Wallet } from 'lucide-react';
import { invalidateAccountBalances, applyOptimisticBalanceDelta, restoreOptimisticSnapshot, beginSync, endSync } from '../hooks/useAccountBalances';

function Calculator() {
  const [income, setIncome] = useState('');
  const [overrides, setOverrides] = useState('');
  const [payDate, setPayDate] = useState('');
  const [notes, setNotes] = useState('');
  const [plan, setPlan] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [allocations, setAllocations] = useState(null);
  const [notification, setNotification] = useState(null);
  const [postCheckSummary, setPostCheckSummary] = useState(null);
  const [currentExpected, setCurrentExpected] = useState(null); // { personal, business, taxes, savings, custom_allocations: {} }

  useEffect(() => {
    loadFinancialPlan();
    loadUserInfo();
    loadCurrentExpected();
  }, []);

  const loadCurrentExpected = async () => {
    try {
      const res = await axios.get(`${API}/settings/balance-comparison`);
      const c = res.data?.comparison || {};
      const customAllocs = c.custom_allocations || {};
      setCurrentExpected({
        personal: c.personal?.expected || 0,
        business: c.business?.expected || 0,
        taxes: c.taxes?.expected || 0,
        savings: c.savings?.expected || 0,
        custom_allocations: Object.fromEntries(
          Object.entries(customAllocs).map(([name, data]) => [name, data.expected || 0])
        ),
      });
    } catch (err) {
      // Non-fatal — the projected section just won't render
      console.error('Error loading current expected balances:', err);
    }
  };

  const loadUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/user-info`);
      setUserRole(response.data.role || '');
    } catch (err) {
      console.error('Error loading user info:', err);
    }
  };

  useEffect(() => {
    if ((income || overrides) && plan) {
      calculateAllocations();
    }
  }, [income, overrides, plan]);

  const loadFinancialPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/financial-plan`);
      setPlan(response.data);
    } catch (err) {
      console.error('Error loading financial plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateAllocations = () => {
    // The user's "total weekly paycheck" is Personal Advance + Overrides.
    // Warnings, allocation preview and target status must all use this
    // combined figure — Overrides on their own can push a paycheck over the
    // recommended target and suppress a spurious "under target" warning.
    const personalAdvance = parseFloat(income) || 0;
    const overridesValue = parseFloat(overrides) || 0;
    const incomeValue = personalAdvance + overridesValue;
    const recommendedTarget = plan?.recommended_weekly_target || 0;
    
    // Get allocation percentages from user's financial plan (convert to decimals)
    const allocation = plan?.allocation || { taxes: 20, business: 20, personal: 55, savings: 5, custom_allocations: [] };
    const taxesPct = allocation.taxes / 100;
    const businessPct = allocation.business / 100;
    const personalPct = allocation.personal / 100;
    const savingsPct = allocation.savings / 100;
    
    // Calculate custom allocations total
    const customAllocations = allocation.custom_allocations || [];
    const customTotal = customAllocations.reduce((sum, ca) => sum + ca.percentage, 0) / 100;
    
    // Determine if over/under target
    const overTarget = incomeValue > recommendedTarget;
    const targetDifference = incomeValue - recommendedTarget;
    
    let taxes, business, personal, savings, buffer, custom;
    
    if (overTarget) {
      // Smart allocation: base on target, excess to buffer
      const baseIncome = recommendedTarget;
      const excess = incomeValue - recommendedTarget;
      
      taxes = baseIncome * taxesPct;
      business = baseIncome * businessPct;
      personal = baseIncome * personalPct;
      savings = baseIncome * savingsPct;
      custom = baseIncome * customTotal;
      buffer = excess;
    } else {
      // Normal allocation
      taxes = incomeValue * taxesPct;
      business = incomeValue * businessPct;
      personal = incomeValue * personalPct;
      savings = incomeValue * savingsPct;
      custom = incomeValue * customTotal;
      buffer = 0;
    }

    const businessReqMet = business >= (plan?.weekly_business_required || 0);
    const personalReqMet = personal >= (plan?.weekly_personal_required || 0);
    const netAfterExpenses = personal - (plan?.weekly_personal_required || 0);

    setAllocations({
      taxes,
      business,
      personal,
      savings,
      buffer,
      businessReqMet,
      personalReqMet,
      netAfterExpenses,
      overTarget,
      targetDifference,
      recommendedTarget,
      totalWeeklyIncome: incomeValue
    });
    
    // Set notification based on target status
    if (incomeValue > 0) {
      if (overTarget) {
        setNotification({
          type: 'success',
          message: `🎉 Over target! Excess $${targetDifference.toFixed(2)} goes to Buffer Pod`,
        });
      } else if (incomeValue < recommendedTarget) {
        const shortfall = recommendedTarget - incomeValue;
        setNotification({
          type: 'warning',
          message: `⚠️ Under target by $${shortfall.toFixed(2)}. Consider increasing income.`,
        });
      } else {
        setNotification({
          type: 'info',
          message: '✅ Perfect! Right at your recommended target.',
        });
      }
    } else {
      setNotification(null);
    }
  };

  const logIncome = async () => {
    if (!income || parseFloat(income) <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    if (!plan) {
      alert('Please set up your financial plan first');
      return;
    }

    // --- OPTIMISTIC UPDATE ---
    // Use the live-preview `allocations` (already split by the user's plan) as
    // the expected delta. Apply immediately so Dashboard + Accounts feel instant.
    //
    // Buffer is a first-class bucket (Feb 2026). The live preview splits the
    // full income by plan%, and the canonical engine's read-time clamp will
    // re-attribute any above-True-Minimum portion to Buffer when the server
    // response lands; for the optimistic beat we apply the plan% split and
    // let the subsequent `invalidateAccountBalances()` reconcile to truth.
    let snapshot = null;
    if (allocations) {
      snapshot = applyOptimisticBalanceDelta({
        operations:      allocations.personal || 0,
        business_growth: allocations.business || 0,
        taxes:           allocations.taxes    || 0,
        savings:         allocations.savings  || 0,
        buffer:          0,
      });
    }

    // Start the sync-status pill
    beginSync('Logging income');

    try {
      setSubmitting(true);
      const incomeResponse = await axios.post(`${API}/income/weekly`, {
        income: parseFloat(income),
        overrides: parseFloat(overrides) || 0.0,
        pay_date: payDate || null,
        notes: notes
      });

      // Fetch expected balances after logging income
      try {
        const balanceResponse = await axios.get(`${API}/settings/balance-comparison`);
        const comparison = balanceResponse.data.comparison;
        const customAllocs = balanceResponse.data.comparison.custom_allocations || {};
        
        // Build summary with this check's allocations and new expected totals
        const thisCheckAllocations = incomeResponse.data;
        setPostCheckSummary({
          thisCheck: {
            personal: thisCheckAllocations.personal || 0,
            business: thisCheckAllocations.business || 0,
            taxes: thisCheckAllocations.taxes || 0,
            savings: thisCheckAllocations.savings || 0,
            custom_allocations: thisCheckAllocations.custom_allocations || {}
          },
          expectedTotals: {
            personal: comparison.personal?.expected || 0,
            business: comparison.business?.expected || 0,
            taxes: comparison.taxes?.expected || 0,
            savings: comparison.savings?.expected || 0,
            custom_allocations: Object.fromEntries(
              Object.entries(customAllocs).map(([name, data]) => [name, data.expected || 0])
            )
          }
        });
      } catch (balanceErr) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Error fetching balance summary:', balanceErr);
        }
      }

      setSuccess(true);
      setIncome('');
      setOverrides('');
      setPayDate('');
      setNotes('');
      setAllocations(null);

      // Refresh projected current-expected so the next preview starts from updated totals
      loadCurrentExpected();

      // Reconcile with server truth — hard invalidate so every view pulls the
      // authoritative numbers. If server differs from optimistic delta, UI
      // snaps to the correct values.
      invalidateAccountBalances();
      endSync({ success: true });

      // Keep summary visible longer, then fade out
      setTimeout(() => {
        setSuccess(false);
        setPostCheckSummary(null);
      }, 15000);
    } catch (err) {
      // Roll back the optimistic update cleanly
      if (snapshot) restoreOptimisticSnapshot(snapshot);
      endSync({ success: false });
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Error logging income:', err);
      }
      alert('Failed to log income — balances restored. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="calculator-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6 max-w-2xl mx-auto" data-testid="no-plan-warning">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mt-1" />
          <div>
            <h2 className="text-lg font-bold text-yellow-900 dark:text-yellow-200 mb-2">Financial Plan Required</h2>
            <p className="text-yellow-800 dark:text-yellow-300 mb-4">
              You need to set up your financial plan before logging income. This is a one-time setup where you enter your monthly bills and business expenses.
            </p>
            <a
              href="/setup"
              className="inline-block px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
            >
              Go to Financial Plan Setup
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="calculator">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold mb-2">Weekly Income Calculator</h1>
        <p className="text-green-100">STEP 2: Enter your weekly paycheck and log it to the tracker</p>
        <p className="text-green-200 text-sm mt-2">💡 Tip: You can backdate entries by selecting a custom pay date (great for entering historical data from the beginning of the year!)</p>
      </div>

      {/* Success Message with Post-Check Summary */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4 space-y-4" data-testid="success-message">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="text-green-800 dark:text-green-300 font-medium">Income logged successfully!</span>
          </div>
          
          {/* Post-Check Summary */}
          {postCheckSummary && (
            <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-700">
              <div className="flex items-center space-x-2 mb-3">
                <Wallet className="w-5 h-5 text-green-600 dark:text-green-400" />
                <span className="font-semibold text-green-800 dark:text-green-200">After this check, you should have:</span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Personal */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Personal</div>
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    ${postCheckSummary.expectedTotals.personal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400">
                    +${postCheckSummary.thisCheck.personal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                </div>
                
                {/* Business */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Business</div>
                  <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    ${postCheckSummary.expectedTotals.business.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400">
                    +${postCheckSummary.thisCheck.business.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                </div>
                
                {/* Taxes */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Taxes</div>
                  <div className="text-lg font-bold text-red-600 dark:text-red-400">
                    ${postCheckSummary.expectedTotals.taxes.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400">
                    +${postCheckSummary.thisCheck.taxes.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                </div>
                
                {/* Savings */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-gray-700">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Savings</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    ${postCheckSummary.expectedTotals.savings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400">
                    +${postCheckSummary.thisCheck.savings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </div>
                </div>
                
                {/* Custom Allocations */}
                {Object.entries(postCheckSummary.expectedTotals.custom_allocations || {}).map(([name, total]) => (
                  <div key={name} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-gray-700">
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{name}</div>
                    <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                      ${total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">
                      +${(postCheckSummary.thisCheck.custom_allocations?.[name] || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </div>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 italic">
                💡 Go to Settings → Balances to compare these expected amounts with your actual bank balances
              </p>
            </div>
          )}
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`rounded-lg p-4 mb-6 ${
          notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-800 dark:text-green-300' :
          notification.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-300' :
          'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-blue-800 dark:text-blue-300'
        }`}>
          <p className="font-medium">{notification.message}</p>
        </div>
      )}

      {/* Income Input */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
          Personal Advance
        </label>
        <div className="relative">
          <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
          <input
            type="number"
            value={income}
            onChange={(e) => setIncome(e.target.value)}
            placeholder="0.00"
            className="w-full pl-12 pr-4 py-4 text-2xl font-bold border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-green-50 dark:bg-green-900/20 text-gray-900 dark:text-white"
            data-testid="income-input"
          />
        </div>
        
        {/* Overrides Input - Only for Leaders */}
        {(userRole === 'satellite' || userRole === 'fsl' || userRole === 'fta') && (
          <div className="mt-4">
            <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
              Overrides
            </label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
              <input
                type="number"
                value={overrides}
                onChange={(e) => setOverrides(e.target.value)}
                placeholder="0.00"
                className="w-full pl-12 pr-4 py-4 text-2xl font-bold border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-purple-50 dark:bg-purple-900/20 text-gray-900 dark:text-white"
                data-testid="overrides-input"
              />
            </div>
          </div>
        )}
        <div className="mt-4">
          <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
            Pay Date (Optional - defaults to today)
          </label>
          <input
            type="date"
            value={payDate}
            onChange={(e) => setPayDate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white"
            data-testid="pay-date-input"
          />
        </div>
        <div className="mt-4">
          <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">
            Notes (Optional)
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this paycheck..."
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            data-testid="notes-input"
          />
        </div>
      </div>

      {/* Your Targets */}
      {(() => {
        // Safe, dynamic percentage normalisation — falls back to 0 if a
        // plan field is missing so we never render undefined%/NaN%.
        const pct = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
        };
        const alloc = plan?.allocation || {};
        const personalPct = pct(alloc.personal);
        const businessPct = pct(alloc.business);
        const taxesPct = pct(alloc.taxes);
        const savingsPct = pct(alloc.savings);
        const customs = alloc.custom_allocations || [];
        const customPctTotal = pct(
          customs.reduce((s, ca) => s + (Number(ca.percentage) || 0), 0)
        );
        // Buffer % is whatever is left over after the core 4 buckets + customs,
        // clamped to [0, 100] so an over-allocated plan never renders negative.
        const bufferPct = pct(
          Math.max(0, 100 - personalPct - businessPct - taxesPct - savingsPct - customPctTotal)
        );
        const personalReq = Number(plan?.weekly_personal_required) || 0;
        const businessReq = Number(plan?.weekly_business_required) || 0;
        const recommended = Number(plan?.recommended_weekly_target) || 0;
        return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6" data-testid="weekly-targets-section">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Your Weekly Targets</h2>

        {/* Explanation Box — fully dynamic, mirrors Financial Plan explainer */}
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded" data-testid="weekly-targets-banner">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-relaxed">
            <strong>💰 Money Flow:</strong> Your income is split by your saved plan:{' '}
            <strong>Personal Account ({personalPct}%)</strong> funds your <strong>${personalReq.toFixed(2)}</strong> weekly living expenses,{' '}
            <strong>Business Account ({businessPct}%)</strong> funds your <strong>${businessReq.toFixed(2)}</strong> weekly business costs,{' '}
            <strong>Taxes Account ({taxesPct}%)</strong>, <strong>Savings Account ({savingsPct}%)</strong>
            {customs.length === 1 && (
              <span>, plus <strong>{customs[0].name} ({pct(customs[0].percentage)}%)</strong></span>
            )}
            {customs.length > 1 && (
              <span>, plus custom allocations totaling <strong>{customPctTotal}%</strong></span>
            )}
            {bufferPct > 0 && (
              <span>, with <strong>{bufferPct}%</strong> held as Buffer</span>
            )}
            . Your recommended weekly income target of <strong>${recommended.toFixed(2)}</strong> is the total income needed <em>before</em> the split so each required bucket is fully funded.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Personal Bills Needed</p>
            <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">Your actual weekly personal bills</p>
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-400" data-testid="weekly-personal-required">${personalReq.toFixed(2)}</p>
          </div>
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Business Expenses Needed</p>
            <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">Your actual weekly business expenses</p>
            <p className="text-2xl font-bold text-purple-700 dark:text-purple-400" data-testid="weekly-business-required">${businessReq.toFixed(2)}</p>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Recommended Weekly Income</p>
            <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-2">This is the total paycheck needed before allocation so your plan fully funds both Personal and Business.</p>
            <p className="text-2xl font-bold text-green-700 dark:text-green-400" data-testid="recommended-weekly-target">${recommended.toFixed(2)}</p>
          </div>
        </div>
      </div>
        );
      })()}

      {/* Allocation Preview */}
      {allocations && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {allocations.overTarget ? 'Smart Allocation with Buffer' : `${plan?.allocation?.taxes ?? 0}/${plan?.allocation?.business ?? 0}/${plan?.allocation?.personal ?? 0}/${plan?.allocation?.savings ?? 0} Allocation Preview`}
          </h2>
          
          {allocations.overTarget && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Great week!</strong> Your income (${allocations.totalWeeklyIncome.toLocaleString()}) exceeds your recommended target (${allocations.recommendedTarget.toLocaleString()}). 
                Base allocation applied to ${allocations.recommendedTarget.toLocaleString()}, and you have an extra ${allocations.targetDifference.toLocaleString()} to allocate.
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <AllocationRow
              label={`Personal Account (${plan?.allocation?.personal ?? 0}%)`}
              amount={allocations.personal}
              color="green"
              testId="allocation-personal"
            />
            <AllocationRow
              label={`Business Account (${plan?.allocation?.business ?? 0}%)`}
              amount={allocations.business}
              color="blue"
              testId="allocation-business"
            />
            <AllocationRow
              label={`Total Taxes After Business Exp. (${plan?.allocation?.taxes ?? 0}%)`}
              amount={allocations.taxes}
              color="red"
              testId="allocation-taxes"
            />
            <AllocationRow
              label={`Savings Account (${plan?.allocation?.savings ?? 0}%)`}
              amount={allocations.savings}
              color="yellow"
              testId="allocation-savings"
            />
            {plan?.allocation?.custom_allocations && plan.allocation.custom_allocations.map((ca, idx) => (
              <AllocationRow
                key={ca.name || `custom-${idx}`}
                label={`${ca.name} (${ca.percentage}%)`}
                amount={allocations.totalWeeklyIncome * (ca.percentage / 100)}
                color="indigo"
                testId={`allocation-custom-${idx}`}
              />
            ))}
          </div>

          {/* Projected Account Totals After This Check (live preview, before saving) */}
          {currentExpected && (
            <div className="mt-6 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg" data-testid="projected-after-check">
              <div className="flex items-center space-x-2 mb-3">
                <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                <span className="font-semibold text-gray-900 dark:text-white">After this check, you should have:</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">(preview — updates as you type)</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ProjectedCard label="Personal" color="blue" current={currentExpected.personal} delta={allocations.personal} testId="projected-personal" />
                <ProjectedCard label="Business" color="purple" current={currentExpected.business} delta={allocations.business} testId="projected-business" />
                <ProjectedCard label="Taxes" color="red" current={currentExpected.taxes} delta={allocations.taxes} testId="projected-taxes" />
                <ProjectedCard label="Savings" color="green" current={currentExpected.savings} delta={allocations.savings} testId="projected-savings" />
                {plan?.allocation?.custom_allocations?.map((ca, idx) => {
                  const delta = allocations.totalWeeklyIncome * (ca.percentage / 100);
                  const current = currentExpected.custom_allocations?.[ca.name] || 0;
                  return (
                    <ProjectedCard
                      key={`proj-${idx}`}
                      label={ca.name}
                      color="orange"
                      current={current}
                      delta={delta}
                      testId={`projected-custom-${idx}`}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Requirements Status */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 rounded-lg border-2 ${
              allocations.businessReqMet
                ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
            }`} data-testid="business-req-status">
              <div className="flex items-center space-x-2 mb-2">
                {allocations.businessReqMet ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
                <p className="font-bold text-gray-900 dark:text-gray-100">
                  Business Req: {allocations.businessReqMet ? 'YES' : 'NO'}
                </p>
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Need ${plan.weekly_business_required.toFixed(2)} | Got ${allocations.business.toFixed(2)}
              </p>
            </div>

            <div className={`p-4 rounded-lg border-2 ${
              allocations.personalReqMet
                ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
            }`} data-testid="personal-req-status">
              <div className="flex items-center space-x-2 mb-2">
                {allocations.personalReqMet ? (
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
                <p className="font-bold text-gray-900 dark:text-gray-100">
                  Personal Req: {allocations.personalReqMet ? 'YES' : 'NO'}
                </p>
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Need ${plan.weekly_personal_required.toFixed(2)} | Got ${allocations.personal.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Net After Expenses */}
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm font-bold text-gray-900 dark:text-gray-100 mb-1">Net After Expenses (Buffer)</p>
            <p className={`text-2xl font-bold ${
              allocations.netAfterExpenses >= 0 ? 'text-green-600' : 'text-red-600'
            }`} data-testid="net-after-expenses">
              ${allocations.netAfterExpenses.toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Log Button */}
      <div className="flex justify-center">
        <button
          onClick={logIncome}
          disabled={submitting || !income}
          className="flex items-center space-x-3 px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-lg font-semibold shadow-lg"
          data-testid="log-income-btn"
        >
          {submitting ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <CheckCircle className="w-6 h-6" />
          )}
          <span>{submitting ? 'Logging...' : 'Log Weekly Paycheck'}</span>
        </button>
      </div>
    </div>
  );
}

function AllocationRow({ label, amount, color, testId }) {
  const colorClasses = {
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    cyan: 'bg-cyan-500',
  };

  return (
    <div className="flex items-center space-x-4" data-testid={testId}>
      <div className="w-32">
        <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{label}</p>
      </div>
      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-8 relative">
        <div
          className={`${colorClasses[color]} h-8 rounded-full transition-all flex items-center justify-end pr-3`}
          style={{ width: '100%' }}
        >
          <span className="text-white font-bold text-sm">${amount.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

function ProjectedCard({ label, color, current, delta, testId }) {
  const totalColorMap = {
    blue: 'text-blue-600 dark:text-blue-400',
    purple: 'text-purple-600 dark:text-purple-400',
    red: 'text-red-600 dark:text-red-400',
    green: 'text-green-600 dark:text-green-400',
    orange: 'text-orange-600 dark:text-orange-400',
  };
  const projected = (current || 0) + (delta || 0);
  const fmt = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-emerald-100 dark:border-gray-700" data-testid={testId}>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold ${totalColorMap[color] || 'text-gray-800'}`}>${fmt(projected)}</div>
      <div className="text-xs text-emerald-600 dark:text-emerald-400">+${fmt(delta || 0)}</div>
      <div className="text-[11px] text-gray-400 dark:text-gray-500">Was ${fmt(current || 0)}</div>
    </div>
  );
}

export default Calculator;
